'use client';

/**
 * Dashboard Debug Overlay
 *
 * Admin-only overlay that shows:
 * - Current thread ID and details
 * - Current chatbot ID and details
 * - Message IDs with copy buttons
 * - Folder structure with IDs
 * - User ID
 *
 * Toggle with Ctrl+Shift+D
 */
import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useCallback } from 'react';
import type { Thread, Message, FolderWithChildren } from '@/types/chat';
import type { Chatbot } from '@/types/chatbot';
interface DashboardDebugOverlayProps {
  isAdmin: boolean;
  userId?: string;
  currentThread: Thread | null;
  messages: Message[];
  chatbots: Chatbot[];
  /** The chatbot currently active for this thread (from composer dropdown) */
  activeChatbot: Chatbot | null;
  /** The chatbot selected in the sidebar (for editing) */
  selectedChatbotId: string | null;
  folderTree: FolderWithChildren[];
}
export default function DashboardDebugOverlay(t0) {
  const $ = _c(128);
  const {
    isAdmin,
    userId,
    currentThread,
    messages,
    chatbots,
    activeChatbot,
    selectedChatbotId,
    folderTree
  } = t0;
  const [isVisible, setIsVisible] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set(["thread", "chatbot", "messages"]);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [expandedSections, setExpandedSections] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Date();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [lastUpdated, setLastUpdated] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setLastUpdated(new Date());
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = currentThread?.id;
  const t5 = activeChatbot?.id;
  let t6;
  if ($[3] !== chatbots.length || $[4] !== folderTree.length || $[5] !== messages.length || $[6] !== selectedChatbotId || $[7] !== t4 || $[8] !== t5) {
    t6 = [t4, t5, selectedChatbotId, messages.length, chatbots.length, folderTree.length];
    $[3] = chatbots.length;
    $[4] = folderTree.length;
    $[5] = messages.length;
    $[6] = selectedChatbotId;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  useEffect(t3, t6);
  let t7;
  let t8;
  if ($[10] !== isAdmin) {
    t7 = () => {
      if (!isAdmin) {
        return;
      }
      const handleKeyDown = e => {
        if (e.ctrlKey && e.shiftKey && e.key === "D") {
          e.preventDefault();
          setIsVisible(_temp);
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t8 = [isAdmin];
    $[10] = isAdmin;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = async (text, id) => {
      ;
      try {
        await navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch (t10) {
        const err = t10;
        console.error("Failed to copy:", err);
      }
    };
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const copyToClipboard = t9;
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = section => {
      setExpandedSections(prev_0 => {
        const next = new Set(prev_0);
        if (next.has(section)) {
          next.delete(section);
        } else {
          next.add(section);
        }
        return next;
      });
    };
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const toggleSection = t10;
  if (!isAdmin || !isVisible) {
    return null;
  }
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[15] !== activeChatbot || $[16] !== copiedId || $[17] !== currentThread || $[18] !== expandedSections || $[19] !== folderTree || $[20] !== lastUpdated || $[21] !== messages || $[22] !== userId) {
    const flattenFolders = (nodes, t23) => {
      const depth = t23 === undefined ? 0 : t23;
      const result = [];
      for (const folder of nodes) {
        result.push({
          folder,
          depth
        });
        if (folder.children && folder.children.length > 0) {
          result.push(...flattenFolders(folder.children, depth + 1));
        }
      }
      return result;
    };
    const flatFolders = flattenFolders(folderTree);
    t22 = "fixed top-4 right-4 z-[9999] pointer-events-auto";
    t19 = "bg-slate-900/95 text-white p-4 rounded-lg shadow-2xl border border-slate-700 max-w-sm max-h-[85vh] overflow-hidden flex flex-col";
    let t24;
    let t25;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <span className="text-yellow-400 text-lg">🔧</span>;
      t25 = <h3 className="font-bold text-sm">Admin Debug Panel</h3>;
      $[35] = t24;
      $[36] = t25;
    } else {
      t24 = $[35];
      t25 = $[36];
    }
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <div className="flex items-center justify-between mb-3"><div className="flex items-center gap-2">{t24}{t25}<div className="flex items-center gap-1 bg-green-500/20 px-2 py-0.5 rounded-full"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" /></span><span className="text-[10px] font-medium text-green-400">LIVE</span></div></div><button onClick={() => setIsVisible(false)} className="text-slate-400 hover:text-white p-1">✕</button></div>;
      $[37] = t20;
    } else {
      t20 = $[37];
    }
    let t26;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <span>Press <kbd className="bg-slate-800 px-1 rounded">Ctrl+Shift+D</kbd> to toggle</span>;
      $[38] = t26;
    } else {
      t26 = $[38];
    }
    let t27;
    if ($[39] !== lastUpdated) {
      t27 = lastUpdated.toLocaleTimeString();
      $[39] = lastUpdated;
      $[40] = t27;
    } else {
      t27 = $[40];
    }
    if ($[41] !== t27) {
      t21 = <div className="text-xs text-slate-500 mb-3 pb-2 border-b border-slate-700 flex items-center justify-between">{t26}<span className="text-slate-600">Updated: {t27}</span></div>;
      $[41] = t27;
      $[42] = t21;
    } else {
      t21 = $[42];
    }
    t14 = "flex-1 overflow-y-auto space-y-3 pr-1";
    if ($[43] !== copiedId || $[44] !== userId) {
      t15 = userId && <div className="bg-slate-800/50 rounded-lg p-2"><div className="flex items-center justify-between"><span className="text-xs text-slate-400">User ID</span><button onClick={() => copyToClipboard(userId, "user")} className="text-slate-400 hover:text-white px-1" title="Copy User ID">{copiedId === "user" ? "\u2713" : "\uD83D\uDCCB"}</button></div><div className="text-xs font-mono text-slate-300 truncate mt-1">{userId}</div></div>;
      $[43] = copiedId;
      $[44] = userId;
      $[45] = t15;
    } else {
      t15 = $[45];
    }
    let t28;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = () => toggleSection("thread");
      $[46] = t28;
    } else {
      t28 = $[46];
    }
    let t29;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <div className="flex items-center gap-2"><span className="w-2 h-2 bg-green-400 rounded-full" /><span className="text-xs font-bold">Current Thread</span></div>;
      $[47] = t29;
    } else {
      t29 = $[47];
    }
    const t30 = expandedSections.has("thread") ? "\u25BC" : "\u25B6";
    let t31;
    if ($[48] !== t30) {
      t31 = <button onClick={t28} className="w-full flex items-center justify-between p-2 hover:bg-slate-700/50">{t29}<span className="text-slate-400 text-xs">{t30}</span></button>;
      $[48] = t30;
      $[49] = t31;
    } else {
      t31 = $[49];
    }
    let t32;
    if ($[50] !== copiedId || $[51] !== currentThread || $[52] !== expandedSections) {
      t32 = expandedSections.has("thread") && <div className="px-2 pb-2">{currentThread ? <div className="space-y-1"><div className="flex items-center justify-between"><span className="text-xs text-slate-400">ID:</span><div className="flex items-center gap-1"><span className="text-xs font-mono text-green-300 truncate max-w-[180px]">{currentThread.id}</span><button onClick={() => copyToClipboard(currentThread.id, "thread")} className="text-slate-400 hover:text-white px-1">{copiedId === "thread" ? "\u2713" : "\uD83D\uDCCB"}</button></div></div><div className="text-xs"><span className="text-slate-400">Title:</span>{" "}<span className="text-slate-200">{currentThread.title || "(untitled)"}</span></div>{currentThread.folder_id && <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Folder ID:</span><div className="flex items-center gap-1"><span className="text-xs font-mono text-slate-300 truncate max-w-[140px]">{currentThread.folder_id}</span><button onClick={() => copyToClipboard(currentThread.folder_id, "thread-folder")} className="text-slate-400 hover:text-white px-1">{copiedId === "thread-folder" ? "\u2713" : "\uD83D\uDCCB"}</button></div></div>}{currentThread.chatbot_id && <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Chatbot ID:</span><div className="flex items-center gap-1"><span className="text-xs font-mono text-slate-300 truncate max-w-[140px]">{currentThread.chatbot_id}</span><button onClick={() => copyToClipboard(currentThread.chatbot_id, "thread-chatbot")} className="text-slate-400 hover:text-white px-1">{copiedId === "thread-chatbot" ? "\u2713" : "\uD83D\uDCCB"}</button></div></div>}</div> : <div className="text-xs text-slate-500 italic">No thread selected</div>}</div>;
      $[50] = copiedId;
      $[51] = currentThread;
      $[52] = expandedSections;
      $[53] = t32;
    } else {
      t32 = $[53];
    }
    if ($[54] !== t31 || $[55] !== t32) {
      t16 = <div className="bg-slate-800/50 rounded-lg overflow-hidden">{t31}{t32}</div>;
      $[54] = t31;
      $[55] = t32;
      $[56] = t16;
    } else {
      t16 = $[56];
    }
    let t33;
    if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = () => toggleSection("chatbot");
      $[57] = t33;
    } else {
      t33 = $[57];
    }
    let t34;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <div className="flex items-center gap-2"><span className="w-2 h-2 bg-purple-400 rounded-full" /><span className="text-xs font-bold">Active Chatbot (Thread)</span></div>;
      $[58] = t34;
    } else {
      t34 = $[58];
    }
    const t35 = expandedSections.has("chatbot") ? "\u25BC" : "\u25B6";
    let t36;
    if ($[59] !== t35) {
      t36 = <button onClick={t33} className="w-full flex items-center justify-between p-2 hover:bg-slate-700/50">{t34}<span className="text-slate-400 text-xs">{t35}</span></button>;
      $[59] = t35;
      $[60] = t36;
    } else {
      t36 = $[60];
    }
    let t37;
    if ($[61] !== activeChatbot || $[62] !== copiedId || $[63] !== expandedSections) {
      t37 = expandedSections.has("chatbot") && <div className="px-2 pb-2">{activeChatbot ? <div className="space-y-1"><div className="flex items-center justify-between"><span className="text-xs text-slate-400">ID:</span><div className="flex items-center gap-1"><span className="text-xs font-mono text-purple-300 truncate max-w-[180px]">{activeChatbot.id}</span><button onClick={() => copyToClipboard(activeChatbot.id, "chatbot")} className="text-slate-400 hover:text-white px-1">{copiedId === "chatbot" ? "\u2713" : "\uD83D\uDCCB"}</button></div></div><div className="text-xs"><span className="text-slate-400">Name:</span>{" "}<span className="text-slate-200">{activeChatbot.name}</span></div>{activeChatbot.folder_id && <div className="flex items-center justify-between"><span className="text-xs text-slate-400">Folder ID:</span><div className="flex items-center gap-1"><span className="text-xs font-mono text-slate-300 truncate max-w-[140px]">{activeChatbot.folder_id}</span><button onClick={() => copyToClipboard(activeChatbot.folder_id, "chatbot-folder")} className="text-slate-400 hover:text-white px-1">{copiedId === "chatbot-folder" ? "\u2713" : "\uD83D\uDCCB"}</button></div></div>}</div> : <div className="text-xs text-amber-400/80 flex items-center gap-1"><span>⚡</span><span>Using Defaults (no chatbot assigned to thread)</span></div>}</div>;
      $[61] = activeChatbot;
      $[62] = copiedId;
      $[63] = expandedSections;
      $[64] = t37;
    } else {
      t37 = $[64];
    }
    if ($[65] !== t36 || $[66] !== t37) {
      t17 = <div className="bg-slate-800/50 rounded-lg overflow-hidden">{t36}{t37}</div>;
      $[65] = t36;
      $[66] = t37;
      $[67] = t17;
    } else {
      t17 = $[67];
    }
    let t38;
    if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
      t38 = () => toggleSection("messages");
      $[68] = t38;
    } else {
      t38 = $[68];
    }
    let t39;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t39 = <span className="w-2 h-2 bg-blue-400 rounded-full" />;
      $[69] = t39;
    } else {
      t39 = $[69];
    }
    let t40;
    if ($[70] !== messages.length) {
      t40 = <div className="flex items-center gap-2">{t39}<span className="text-xs font-bold">Messages ({messages.length})</span></div>;
      $[70] = messages.length;
      $[71] = t40;
    } else {
      t40 = $[71];
    }
    const t41 = expandedSections.has("messages") ? "\u25BC" : "\u25B6";
    let t42;
    if ($[72] !== t41) {
      t42 = <span className="text-slate-400 text-xs">{t41}</span>;
      $[72] = t41;
      $[73] = t42;
    } else {
      t42 = $[73];
    }
    let t43;
    if ($[74] !== t40 || $[75] !== t42) {
      t43 = <button onClick={t38} className="w-full flex items-center justify-between p-2 hover:bg-slate-700/50">{t40}{t42}</button>;
      $[74] = t40;
      $[75] = t42;
      $[76] = t43;
    } else {
      t43 = $[76];
    }
    let t44;
    if ($[77] !== copiedId || $[78] !== expandedSections || $[79] !== messages) {
      t44 = expandedSections.has("messages") && <div className="px-2 pb-2 max-h-48 overflow-y-auto">{messages.length > 0 ? <div className="space-y-1">{messages.map((msg, idx) => <div key={msg.id} className="flex items-center justify-between py-1 border-b border-slate-700/50 last:border-0"><div className="flex items-center gap-2 min-w-0"><span className={`text-xs font-medium ${msg.role === "user" ? "text-blue-300" : "text-green-300"}`}>M{idx + 1}</span><span className="text-xs text-slate-400 truncate max-w-[100px]">{msg.role}</span></div><div className="flex items-center gap-1"><span className="text-xs font-mono text-slate-500 truncate max-w-[100px]">{msg.id.slice(0, 8)}...</span><button onClick={() => copyToClipboard(msg.id, `msg-${msg.id}`)} className="text-slate-400 hover:text-white px-1">{copiedId === `msg-${msg.id}` ? "\u2713" : "\uD83D\uDCCB"}</button></div></div>)}</div> : <div className="text-xs text-slate-500 italic">No messages</div>}</div>;
      $[77] = copiedId;
      $[78] = expandedSections;
      $[79] = messages;
      $[80] = t44;
    } else {
      t44 = $[80];
    }
    if ($[81] !== t43 || $[82] !== t44) {
      t18 = <div className="bg-slate-800/50 rounded-lg overflow-hidden">{t43}{t44}</div>;
      $[81] = t43;
      $[82] = t44;
      $[83] = t18;
    } else {
      t18 = $[83];
    }
    t11 = "bg-slate-800/50 rounded-lg overflow-hidden";
    let t45;
    if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
      t45 = () => toggleSection("folders");
      $[84] = t45;
    } else {
      t45 = $[84];
    }
    let t46;
    if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
      t46 = <span className="w-2 h-2 bg-yellow-400 rounded-full" />;
      $[85] = t46;
    } else {
      t46 = $[85];
    }
    const t47 = <div className="flex items-center gap-2">{t46}<span className="text-xs font-bold">Folders ({flatFolders.length})</span></div>;
    const t48 = expandedSections.has("folders") ? "\u25BC" : "\u25B6";
    let t49;
    if ($[86] !== t48) {
      t49 = <span className="text-slate-400 text-xs">{t48}</span>;
      $[86] = t48;
      $[87] = t49;
    } else {
      t49 = $[87];
    }
    if ($[88] !== t47 || $[89] !== t49) {
      t12 = <button onClick={t45} className="w-full flex items-center justify-between p-2 hover:bg-slate-700/50">{t47}{t49}</button>;
      $[88] = t47;
      $[89] = t49;
      $[90] = t12;
    } else {
      t12 = $[90];
    }
    t13 = expandedSections.has("folders") && <div className="px-2 pb-2 max-h-48 overflow-y-auto">{flatFolders.length > 0 ? <div className="space-y-1">{flatFolders.map(t50 => {
          const {
            folder: folder_0,
            depth: depth_0
          } = t50;
          return <div key={folder_0.id} className="flex items-center justify-between py-1 border-b border-slate-700/50 last:border-0" style={{
            paddingLeft: `${depth_0 * 12}px`
          }}><div className="flex items-center gap-2 min-w-0"><span className="text-xs">📁</span><span className="text-xs text-slate-200 truncate max-w-[120px]">{folder_0.name}</span></div><div className="flex items-center gap-1"><span className="text-xs font-mono text-slate-500 truncate max-w-[80px]">{folder_0.id.slice(0, 8)}...</span><button onClick={() => copyToClipboard(folder_0.id, `folder-${folder_0.id}`)} className="text-slate-400 hover:text-white px-1">{copiedId === `folder-${folder_0.id}` ? "\u2713" : "\uD83D\uDCCB"}</button></div></div>;
        })}</div> : <div className="text-xs text-slate-500 italic">No folders</div>}</div>;
    $[15] = activeChatbot;
    $[16] = copiedId;
    $[17] = currentThread;
    $[18] = expandedSections;
    $[19] = folderTree;
    $[20] = lastUpdated;
    $[21] = messages;
    $[22] = userId;
    $[23] = t11;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
    $[29] = t17;
    $[30] = t18;
    $[31] = t19;
    $[32] = t20;
    $[33] = t21;
    $[34] = t22;
  } else {
    t11 = $[23];
    t12 = $[24];
    t13 = $[25];
    t14 = $[26];
    t15 = $[27];
    t16 = $[28];
    t17 = $[29];
    t18 = $[30];
    t19 = $[31];
    t20 = $[32];
    t21 = $[33];
    t22 = $[34];
  }
  let t23;
  if ($[91] !== t11 || $[92] !== t12 || $[93] !== t13) {
    t23 = <div className={t11}>{t12}{t13}</div>;
    $[91] = t11;
    $[92] = t12;
    $[93] = t13;
    $[94] = t23;
  } else {
    t23 = $[94];
  }
  let t24;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => toggleSection("all-chatbots");
    $[95] = t24;
  } else {
    t24 = $[95];
  }
  let t25;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <span className="w-2 h-2 bg-pink-400 rounded-full" />;
    $[96] = t25;
  } else {
    t25 = $[96];
  }
  let t26;
  if ($[97] !== chatbots.length) {
    t26 = <div className="flex items-center gap-2">{t25}<span className="text-xs font-bold">All Chatbots ({chatbots.length})</span></div>;
    $[97] = chatbots.length;
    $[98] = t26;
  } else {
    t26 = $[98];
  }
  const t27 = expandedSections.has("all-chatbots") ? "\u25BC" : "\u25B6";
  let t28;
  if ($[99] !== t27) {
    t28 = <span className="text-slate-400 text-xs">{t27}</span>;
    $[99] = t27;
    $[100] = t28;
  } else {
    t28 = $[100];
  }
  let t29;
  if ($[101] !== t26 || $[102] !== t28) {
    t29 = <button onClick={t24} className="w-full flex items-center justify-between p-2 hover:bg-slate-700/50">{t26}{t28}</button>;
    $[101] = t26;
    $[102] = t28;
    $[103] = t29;
  } else {
    t29 = $[103];
  }
  let t30;
  if ($[104] !== activeChatbot?.id || $[105] !== chatbots || $[106] !== copiedId || $[107] !== expandedSections) {
    t30 = expandedSections.has("all-chatbots") && <div className="px-2 pb-2 max-h-48 overflow-y-auto">{chatbots.length > 0 ? <div className="space-y-1">{chatbots.map((chatbot, idx_0) => <div key={chatbot.id} className={`flex items-center justify-between py-1 border-b border-slate-700/50 last:border-0 ${chatbot.id === activeChatbot?.id ? "bg-purple-500/20 rounded" : ""}`}><div className="flex items-center gap-2 min-w-0"><span className="text-xs font-medium text-pink-300">C{idx_0 + 1}</span><span className="text-xs text-slate-200 truncate max-w-[100px]">{chatbot.name}</span></div><div className="flex items-center gap-1"><span className="text-xs font-mono text-slate-500 truncate max-w-[80px]">{chatbot.id.slice(0, 8)}...</span><button onClick={() => copyToClipboard(chatbot.id, `all-chatbot-${chatbot.id}`)} className="text-slate-400 hover:text-white px-1">{copiedId === `all-chatbot-${chatbot.id}` ? "\u2713" : "\uD83D\uDCCB"}</button></div></div>)}</div> : <div className="text-xs text-slate-500 italic">No chatbots</div>}</div>;
    $[104] = activeChatbot?.id;
    $[105] = chatbots;
    $[106] = copiedId;
    $[107] = expandedSections;
    $[108] = t30;
  } else {
    t30 = $[108];
  }
  let t31;
  if ($[109] !== t29 || $[110] !== t30) {
    t31 = <div className="bg-slate-800/50 rounded-lg overflow-hidden">{t29}{t30}</div>;
    $[109] = t29;
    $[110] = t30;
    $[111] = t31;
  } else {
    t31 = $[111];
  }
  let t32;
  if ($[112] !== t14 || $[113] !== t15 || $[114] !== t16 || $[115] !== t17 || $[116] !== t18 || $[117] !== t23 || $[118] !== t31) {
    t32 = <div className={t14}>{t15}{t16}{t17}{t18}{t23}{t31}</div>;
    $[112] = t14;
    $[113] = t15;
    $[114] = t16;
    $[115] = t17;
    $[116] = t18;
    $[117] = t23;
    $[118] = t31;
    $[119] = t32;
  } else {
    t32 = $[119];
  }
  let t33;
  if ($[120] !== t19 || $[121] !== t20 || $[122] !== t21 || $[123] !== t32) {
    t33 = <div className={t19}>{t20}{t21}{t32}</div>;
    $[120] = t19;
    $[121] = t20;
    $[122] = t21;
    $[123] = t32;
    $[124] = t33;
  } else {
    t33 = $[124];
  }
  let t34;
  if ($[125] !== t22 || $[126] !== t33) {
    t34 = <div className={t22}>{t33}</div>;
    $[125] = t22;
    $[126] = t33;
    $[127] = t34;
  } else {
    t34 = $[127];
  }
  return t34;
}
function _temp(prev) {
  return !prev;
}